'use client';

import type { ReactNode } from 'react';
import type { DefaultValues, FieldValues } from 'react-hook-form';
import type { ZodType } from 'zod';

import { submitDynamicForm } from '@/lib/client/forms/submitDynamicForm';
import { pushFormSuccessGtmEvent } from '@/lib/analytics/pushFormSuccessGtmEvent';
import { sanitizeClientFormMetadata } from '@/lib/forms/sanitizeClientFormMetadata';
import { isMarketingConsentEnabled } from '@/lib/forms/marketingConsent';

import { DynamicForm } from '@/components/forms/DynamicForm/DynamicForm';
import type { FieldConfig } from '@/types/forms';

export type FormSubmissionModalProps<T extends FieldValues> = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formId: string;
  schema: ZodType<T>;
  fields: FieldConfig[];
  defaultValues: DefaultValues<T>;
  title: ReactNode;
  description?: ReactNode;
  submitLabel?: string;
  columns?: 1 | 2;
  className?: string;
  metadata?: Record<string, unknown>;
  buildMetadata?: () => Record<string, unknown>;
  recipientContext?: {
    doctorSlug?: string;
    doctorApiId?: string | number | null;
  } & Record<string, unknown>;
  security?: {
    honeypot?: boolean;
    honeypotName?: string;
  };
  modal?: {
    closeOnBackdrop?: boolean;
    closeOnEscape?: boolean;
    closeButtonLabel?: string;
    className?: string;
    panelClassName?: string;
    backdropClassName?: string;
    autoCloseOnSuccessMs?: number | false;
  };
};

export function FormSubmissionModal<T extends FieldValues>({
  open,
  onOpenChange,
  formId,
  schema,
  fields,
  defaultValues,
  title,
  description,
  submitLabel = 'Submit',
  columns = 2,
  className,
  metadata,
  buildMetadata,
  recipientContext,
  security = {
    honeypot: true,
    honeypotName: 'lips-name',
  },
  modal,
}: FormSubmissionModalProps<T>) {
  return (
    <DynamicForm<T>
      mode="modal"
      open={open}
      onOpenChange={onOpenChange}
      schema={schema}
      fields={fields}
      defaultValues={defaultValues}
      columns={columns}
      submitLabel={submitLabel}
      className={className}
      security={security}
      showMarketingConsent={isMarketingConsentEnabled(formId)}
      modal={{
        title,
        description,
        ...modal,
      }}
      onSubmit={async (values, context) => {
        await submitDynamicForm({
          formId,
          values,
          metadata: sanitizeClientFormMetadata({
            pageUrl:
              typeof window !== 'undefined' ? window.location.href : undefined,
            ...metadata,
            ...buildMetadata?.(),
          }),
          security: {
            honeypotName: context?.security?.honeypotName,
            honeypot: context?.security?.honeypot,
          },
          recipientContext: recipientContext
            ? {
                doctorSlug: recipientContext.doctorSlug,
                doctorApiId: recipientContext.doctorApiId ?? undefined,
              }
            : undefined,
        });

        pushFormSuccessGtmEvent(formId, {
          doctorName:
            typeof metadata?.doctorName === 'string'
              ? metadata.doctorName
              : undefined,
        });
      }}
    />
  );
}
